import React from 'react';
import { api, Mark, MarkShootingPlanItem, MarkRolePermissions } from '../api.ts';
import { planSpeciesFamilyKey } from '../swedishHuntingLegality';
import { SUBCAT_MAP as WD_SM, FAMILY_DISPLAY_NAMES as WD_FL, SPECIES as WD_SPECIES, SUBCATS as WD_SUBCATS } from '../wildlifeData.ts';

type GenRow={gameType:string;count:number;cost:number;included:boolean};

interface AvskjutningsplanTabProps {
  mark: Mark;
  setMark: (m: Mark) => void;
  planLd: boolean;
  plan: MarkShootingPlanItem[];
  setPlan: React.Dispatch<React.SetStateAction<MarkShootingPlanItem[]>>;
  planSeason: string;
  setPlanSeason: (s: string) => void;
  planEditMode: boolean;
  setPlanEditMode: (v: boolean) => void;
  planDraft: Record<string,{planned:number;cost:number}>;
  setPlanDraft: React.Dispatch<React.SetStateAction<Record<string,{planned:number;cost:number}>>>;
  planSaving: boolean;
  setPlanSaving: (v: boolean) => void;
  planCreating: boolean;
  setPlanCreating: (v: boolean) => void;
  showGenPlan: boolean;
  setShowGenPlan: (v: boolean) => void;
  genRows: GenRow[];
  setGenRows: React.Dispatch<React.SetStateAction<GenRow[]>>;
  genSaving: boolean;
  setGenSaving: (v: boolean) => void;
  newSpeciesName: string;
  setNewSpeciesName: (v: string) => void;
  popLevels: Record<string,number>;
  setPopLevels: React.Dispatch<React.SetStateAction<Record<string,number>>>;
  popCounts: Record<string,number>;
  setPopCounts: React.Dispatch<React.SetStateAction<Record<string,number>>>;
  savePopLevels: (newLvs: Record<string,number>) => void;
  buildPopPlan: () => void;
  isAdmin?: boolean;
  isMarkOwner: boolean;
  myPerms: MarkRolePermissions | null;
  EXCL_SUBCAT: (gt: string) => boolean;
  T: any;
  iS: React.CSSProperties;
  bP: React.CSSProperties;
  bSc: React.CSSProperties;
  secT: React.CSSProperties;
}

export function AvskjutningsplanTab({
  mark, setMark,
  planLd, plan, setPlan, planSeason, setPlanSeason,
  planEditMode, setPlanEditMode, planDraft, setPlanDraft,
  planSaving, setPlanSaving, planCreating, setPlanCreating,
  showGenPlan, setShowGenPlan, genRows, setGenRows, genSaving, setGenSaving,
  newSpeciesName, setNewSpeciesName,
  popLevels, setPopLevels, popCounts, setPopCounts, savePopLevels, buildPopPlan,
  isAdmin, isMarkOwner, myPerms,
  EXCL_SUBCAT,
  T, iS, bP, bSc, secT,
}: AvskjutningsplanTabProps) {
  if (planLd) {
    return <div style={{color:T.textMuted,fontSize:14,padding:'20px 0'}}>Laddar avskjutningsplan...</div>;
  }

  /**
   * Tar bort ett viltslag ur viltvårdsplanen (V2). Hela familjen, eller sista raden för den,
   * tas också bort ur markens viltlista – då syns den varken i skjutmenyn eller annonsen.
   */
  const removeGame = async (gt: string, whole: boolean) => {
    const fam = planSpeciesFamilyKey(gt);
    const famRows = plan.filter(r => r.season === planSeason && planSpeciesFamilyKey(r.gameType) === fam);
    const all = whole || famRows.every(r => r.gameType === gt);
    const famLabel = WD_FL[fam] ?? gt.split(' — ')[0];
    const sub = gt.includes(' — ') ? gt.split(' — ').slice(1).join(' — ') : gt;
    const question = all
      ? `Ta bort ${famLabel} ur viltvårdsplanen? ${famLabel} försvinner ur skjutmenyn och annonsen. Redan fällda djur påverkas inte.`
      : `Ta bort ${sub} ur säsongens plan? Den syns då inte i skjutmenyn.`;
    if (!window.confirm(question)) return;
    try {
      const m = await api.removeShootingPlanGame(mark.id, planSeason, gt, whole);
      const gone = (r: MarkShootingPlanItem) => r.season === planSeason && (all ? planSpeciesFamilyKey(r.gameType) === fam : r.gameType === gt);
      setPlan(prev => prev.filter(r => !gone(r)));
      setPlanDraft(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => (all ? planSpeciesFamilyKey(k) !== fam : k !== gt))));
      setMark({ ...mark, gameTypesJson: m.gameTypesJson });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Viltslaget kunde inte tas bort');
    }
  };

  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <div style={secT}>Avskjutningsplan</div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {planEditMode
              ? <>
                  <button onClick={()=>setPlanEditMode(false)} style={{...bSc,fontSize:12,padding:'6px 14px'}}>Avbryt</button>
                  <button disabled={planSaving} onClick={async()=>{
                    setPlanSaving(true);
                    try{
                      const updates=await Promise.all(Object.entries(planDraft).map(([gt,d])=>{
                        const existing=plan.find(r=>r.season===planSeason&&r.gameType===gt);
                        if(!existing&&d.planned===0)return Promise.resolve(null);
                        return api.upsertMarkShootingPlan({markId:mark.id,season:planSeason,gameType:gt,plannedCount:d.planned});
                      }));
                      const valid=updates.filter(Boolean) as MarkShootingPlanItem[];
                      setPlan(prev=>{let next=[...prev];for(const u of valid){const i=next.findIndex(p=>p.id===u.id);if(i>=0)next[i]={...next[i],...u};else next.push(u);}return next;});
                      setPlanEditMode(false);
                    }catch{}finally{setPlanSaving(false);}
                  }} style={{...bP,fontSize:12,padding:'6px 18px',opacity:planSaving?0.6:1}}>{planSaving?'Sparar...':'Spara'}</button>
                </>
              : <>
                  {(isAdmin||myPerms?.canManageShootingPlan||isMarkOwner)&&<button onClick={()=>{
                    const seasonPlanNow=plan.filter(r=>r.season===planSeason);
                    const draft:Record<string,{planned:number;cost:number}>={};
                    for(const r of seasonPlanNow)draft[r.gameType]={planned:r.plannedCount,cost:r.costPerAnimal||0};
                    setPlanDraft(draft);setPlanEditMode(true);
                  }} style={{...bSc,fontSize:12,padding:'6px 14px'}}>Redigera plan</button>}
                  {(isAdmin||myPerms?.canManageShootingPlan||isMarkOwner)&&!showGenPlan&&<button onClick={buildPopPlan} style={{...bSc,fontSize:12,padding:'6px 14px',background:'rgba(193,127,58,0.12)',color:T.amber,borderColor:T.amber}}>✦ Generera förslag</button>}
                  {(isAdmin||myPerms?.canManageShootingPlan||isMarkOwner)&&(()=>{const pts=planSeason.split('/').map(Number);const ey=pts.length>=2?pts[pts.length-1]:(!isNaN(pts[0])?pts[0]:new Date().getFullYear());const ns=`${ey}/${ey+1}`;const hasNext=plan.some(r=>r.season===ns);return!hasNext?<button disabled={planCreating} onClick={async()=>{const rows=plan.filter(r=>r.season===planSeason);if(!rows.length){setPlanSeason(ns);return;}setPlanCreating(true);try{const created=await Promise.all(rows.map(r=>api.upsertMarkShootingPlan({markId:mark.id,season:ns,gameType:r.gameType,plannedCount:r.plannedCount})));setPlan(prev=>[...prev,...created]);setPlanSeason(ns);}catch{}finally{setPlanCreating(false);}}} style={{...bSc,fontSize:12,padding:'6px 14px',opacity:planCreating?0.6:1}}>{planCreating?'Skapar...':'+ Ny säsong'}</button>:null;})()}
                </>
            }
          </div>
        </div>
        <div style={{color:T.textMuted,fontSize:12,lineHeight:1.5,margin:'0 0 10px'}}>
          Priser (fällavgift och kött) sätts under <strong style={{color:T.textSub}}>Ekonomi</strong> per grupp.
          {planEditMode&&<> 0 betyder inget skjutmål – viltslaget är grönt i skjutmenyn när det är lovligt. Får ett viltslag inte jagas alls på marken: <strong style={{color:T.textSub}}>Ta bort viltslag</strong>.</>}
        </div>
        {(()=>{const seasons=[...new Set(plan.map(r=>r.season))].sort();const normS=(s:string)=>{if(s.includes('/'))return s;const yr=Number(s);return isNaN(yr)?s:`${yr-1}/${yr}`;};return seasons.length>0?<div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{seasons.map(s=><button key={s} onClick={()=>{setPlanSeason(s);setPlanEditMode(false);}} style={{...bSc,fontSize:12,padding:'5px 14px',background:s===planSeason?T.amber:'transparent',color:s===planSeason?T.bg:T.textSub,borderColor:s===planSeason?T.amber:T.border}}>{normS(s)}</button>)}</div>:null;})()}
      </div>
      {(()=>{
        const seasonPlan=plan.filter(r=>r.season===planSeason);
        const FL=WD_FL;
        const HIDE=new Set(['tjader','orre']);
        const SM=WD_SM;
        // Reverse map: subcat label → parent family key (for detecting orphan plan items)
        const SUBCPAR:Record<string,string>={};
        for(const[sk,scs] of Object.entries(WD_SUBCATS)) for(const sc of scs as {label:string}[]) SUBCPAR[sc.label]=sk;
        type PGrp={fk:string;label:string;items:string[]};
        const groups:PGrp[]=[]; const seen=new Set<string>();
        const planItems=(planRows:typeof seasonPlan,fk:string)=>{
          const smItems=SM[fk]??[];
          if(planRows.length===0)return smItems;
          const allGts=planRows.map(r=>r.gameType);
          const hasDash=allGts.some(g=>g.includes(' — '));
          // Base: dash-style subcats or plain names
          const base=hasDash?allGts.filter(g=>g.includes(' — ')):allGts;
          // Prefix for supplemented SM items (match format of existing plan items)
          const prefix=hasDash?((WD_FL[fk]??fk)+' — '):'';
          // Add SM items not already represented in base
          const extras=smItems.filter(sm=>{
            const smL=sm.toLowerCase();
            return !base.some(pt=>{
              const ptL=pt.toLowerCase();
              const ptSub=ptL.includes(' — ')?ptL.split(' — ').slice(1).join(' — '):ptL;
              return ptSub===smL||ptL===smL||ptL.endsWith(smL);
            });
          }).map(sm=>prefix+sm);
          return [...base,...extras];
        };
        try{ const j=JSON.parse((mark as any).gameTypesJson||'[]'); if(Array.isArray(j)) for(const raw of j){ const gt=String(raw).trim(); if(!gt) continue; const fk=planSpeciesFamilyKey(gt); if(seen.has(fk)) continue; if(HIDE.has(fk)) continue; seen.add(fk); const planRows=seasonPlan.filter(p=>planSpeciesFamilyKey(p.gameType)===fk); const items=planItems(planRows,fk); if(items.length>0)groups.push({fk,label:FL[fk]??gt,items}); else groups.push({fk,label:FL[fk]??gt,items:[gt]}); } }catch{/**/}
        for(const p of seasonPlan){ const fk=planSpeciesFamilyKey(p.gameType); if(seen.has(fk)) continue; if(HIDE.has(fk)) continue; if(SUBCPAR[p.gameType]) continue; seen.add(fk); const planRows=seasonPlan.filter(r=>planSpeciesFamilyKey(r.gameType)===fk); groups.push({fk,label:FL[fk]??p.gameType,items:planItems(planRows,fk)}); }
        // If the owner has no plan at all, show a full default species list in edit mode so they can start from scratch
        if(isMarkOwner&&planEditMode&&groups.length===0){const DEFAULT_SP=['Älg','Rådjur','Vildsvin','Kronhjort','Dovhjort','Hare','Fasan','Räv'];for(const label of DEFAULT_SP){const fk=planSpeciesFamilyKey(label);if(seen.has(fk))continue;seen.add(fk);groups.push({fk,label,items:[label]});}}
        const allGts=groups.flatMap(g=>g.items.filter(gt=>!EXCL_SUBCAT(gt)));
        const totalPlanned=planEditMode?allGts.reduce((s,gt)=>(planDraft[gt]?.planned??seasonPlan.find(r=>r.gameType===gt)?.plannedCount??0)+s,0):seasonPlan.reduce((s,p)=>s+p.plannedCount,0);
        const totalActual=seasonPlan.reduce((s,p)=>s+p.actualCount,0);
        if(!planEditMode&&groups.length===0&&isMarkOwner)return<div style={{color:T.textSub,fontSize:14,padding:'20px 0',lineHeight:1.7}}>Ingen avskjutningsplan satt ännu. Klicka på <strong style={{color:T.text}}>Redigera plan</strong> för att sätta upp viltstammar och planerat uttag för din mark.</div>;
        if(!planEditMode&&groups.length===0)return<div style={{color:T.textSub,fontSize:14,padding:'20px 0'}}>Ingen avskjutningsplan har skapats för denna mark.</div>;
        // ── Generated plan preview ──
        if(showGenPlan&&genRows.length>0)return<div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <div style={{color:T.amber,fontWeight:700,fontSize:14}}>✦ Förslag baserat på stammar</div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setShowGenPlan(false)} style={{background:'transparent',color:T.textSub,border:`1px solid ${T.border}`,borderRadius:5,padding:'6px 14px',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>Avbryt</button>
              <button disabled={genSaving} onClick={async()=>{
                setGenSaving(true);
                try{
                  const toSave=genRows.filter(r=>r.included&&r.count>0);
                  const saved=await Promise.all(toSave.map(r=>api.upsertMarkShootingPlan({markId:mark.id,season:planSeason,gameType:r.gameType,plannedCount:r.count})));
                  setPlan(prev=>{const next=[...prev];saved.forEach(s=>{const i=next.findIndex(p=>p.id===s.id);if(i>=0)next[i]={...next[i],...s};else next.push(s as any);});return next;});
                  setShowGenPlan(false);
                }catch(e){console.error(e);}finally{setGenSaving(false);}
              }} style={{background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'6px 18px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',opacity:genSaving?0.6:1}}>{genSaving?'Lagrar...':'Lagra plan'}</button>
            </div>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{borderBottom:`2px solid ${T.border}`}}>
              <th style={{textAlign:'left',padding:'8px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase' as const,letterSpacing:'0.1em'}}>Viltslag</th>
              <th style={{textAlign:'center',padding:'8px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase' as const,letterSpacing:'0.1em'}}>Föreslaget uttag</th>
              <th style={{textAlign:'center',padding:'8px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase' as const,letterSpacing:'0.1em'}}>Ta med</th>
            </tr></thead>
            <tbody>{genRows.map((r,i)=><tr key={i} style={{borderBottom:`1px solid ${T.border}`,opacity:r.included?1:0.4}}>
              <td style={{padding:'8px 12px',color:T.text,fontWeight:600}}>{r.gameType.includes(' — ')?<><span style={{color:T.textMuted,fontWeight:400}}>{r.gameType.split(' — ')[0]} </span>{r.gameType.split(' — ').slice(1).join(' — ')}</>:r.gameType}</td>
              <td style={{padding:'8px 12px',textAlign:'center'}}><input type="number" min={0} value={r.count} onChange={e=>setGenRows(prev=>prev.map((x,j)=>j===i?{...x,count:Number(e.target.value)}:x))} style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:4,padding:'3px 6px',color:T.text,fontSize:12,width:60,textAlign:'center' as const,fontFamily:'inherit'}}/></td>
              <td style={{padding:'8px 12px',textAlign:'center'}}><input type="checkbox" checked={r.included} onChange={e=>setGenRows(prev=>prev.map((x,j)=>j===i?{...x,included:e.target.checked}:x))}/></td>
            </tr>)}</tbody>
          </table>
        </div>;
        return <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{borderBottom:`2px solid ${T.border}`}}>
              <th style={{textAlign:'left',padding:'10px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:'0.1em'}}>Viltslag</th>
              <th style={{textAlign:'center',padding:'10px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:'0.1em'}}>Planerat</th>
              <th style={{textAlign:'center',padding:'10px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:'0.1em'}}>Fällt</th>
              {planEditMode&&<th style={{width:1}}/>}
              {!planEditMode&&<th style={{textAlign:'left',padding:'10px 12px',color:T.textMuted,fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:'0.1em'}}>Framsteg</th>}
            </tr></thead>
            <tbody>{groups.flatMap(({fk,label,items})=>{
              const filteredItems=items.filter(gt=>!EXCL_SUBCAT(gt));
              if(filteredItems.length===0)return[];
              const multi=filteredItems.length>1;
              const rows:React.ReactNode[]=[];
              // ── Species header row with population frequency bars + antal djur ──
              const pLvC:{[k:number]:string}={1:'#5a5a5a',2:'#8a7a30',3:'#c5870a',4:'#e67e22',5:'#c0392b'};
              const pLvL:{[k:number]:string}={1:'Sällsynt',2:'Sparsam',3:'Förekommande',4:'Riklig',5:'Extrem'};
              const curLv=popLevels[fk]??0;
              const curCnt=popCounts[fk]??0;
              const spDef=WD_SPECIES.find(s=>s.key===fk);
              const ha=Number((mark as any).areaHa)||100;
              rows.push(<tr key={`h-${fk}`} style={{background:T.surfaceHover,borderTop:`2px solid ${T.border}`}}>
                <td colSpan={4} style={{padding:'7px 12px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontWeight:700,color:T.text,fontSize:13,minWidth:90,flexShrink:0}}>{label}</span>
                    <div style={{display:'flex',gap:3,width:80,flexShrink:0}}>
                      {[1,2,3,4,5].map(n=><div key={n} title={pLvL[n]} onClick={e=>{e.stopPropagation();const lf=[0,0.25,0.5,1.0,2.0,4.0];const dc=Math.round((ha/100)*(spDef?.refPer100Ha??0)*lf[n]);const newLvs={...popLevels,[fk]:n};setPopLevels(newLvs);setPopCounts(prev=>({...prev,[fk]:dc}));savePopLevels(newLvs);}} style={{flex:1,height:9,borderRadius:1,background:n<=curLv?(pLvC[curLv]??T.amber):T.border,cursor:'pointer',transition:'background 0.2s'}}/>)}
                    </div>
                    <span style={{color:pLvC[curLv]??T.textMuted,fontSize:10,fontWeight:600,minWidth:72,flexShrink:0}}>{curLv>0?pLvL[curLv]:''}</span>
                    <div style={{display:'flex',alignItems:'center',gap:4,marginLeft:4}}>
                      <input type="number" min={0} value={curCnt||''} placeholder="Antal djur" onClick={e=>e.stopPropagation()} onChange={e=>setPopCounts(prev=>({...prev,[fk]:Number(e.target.value)}))} style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:4,padding:'3px 6px',color:T.text,fontSize:11,width:72,textAlign:'center' as const,fontFamily:'inherit'}}/>
                      <span style={{color:T.textMuted,fontSize:10}}>djur i stammen</span>
                    </div>
                    {planEditMode&&<button type="button" onClick={e=>{e.stopPropagation();removeGame(label,true);}} style={{marginLeft:'auto',background:'transparent',border:`1px solid ${T.border}`,borderRadius:5,color:T.textSub,fontSize:11,padding:'4px 10px',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>Ta bort viltslag</button>}
                  </div>
                </td>
              </tr>);
              for(const gt of filteredItems){
                const p=seasonPlan.find(r=>r.gameType===gt);
                const planned=p?.plannedCount??0;
                const actual=p?.actualCount??0;
                // Gammalt pris (före V1): bara kvar om det inte kunde flyttas till Ekonomi.
                const oldPrice=p?.costPerAnimal!=null&&p.costPerAnimal>0?p.costPerAnimal:null;
                const dispGt=gt.includes(' — ')?gt.split(' — ').slice(1).join(' — '):gt;
                if(planEditMode){
                  const dv=planDraft[gt]??{planned,cost:0};
                  rows.push(<tr key={`e-${gt}`} style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:'10px 12px',paddingLeft:multi?24:12,color:T.text,fontWeight:600}}>{dispGt}</td>
                    <td style={{padding:'8px 12px',textAlign:'center'}}><input type="number" min={0} style={{...iS,width:60,textAlign:'center',padding:'4px 6px'}} value={dv.planned} onChange={e=>setPlanDraft(prev=>({...prev,[gt]:{...dv,planned:Number(e.target.value)}}))} /></td>
                    <td style={{padding:'10px 12px',textAlign:'center',color:T.textSub}}>{actual}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{p&&<button type="button" title={`Ta bort ${dispGt} ur planen`} aria-label={`Ta bort ${dispGt} ur planen`} onClick={()=>removeGame(gt,false)} style={{background:'transparent',border:'none',color:T.textMuted,fontSize:16,cursor:'pointer',padding:'2px 6px',lineHeight:1}}>×</button>}</td>
                  </tr>);
                } else {
                  const pct=planned>0?Math.min(100,Math.round((actual/planned)*100)):0;
                  const pctColor=pct>=100?T.danger:pct>=75?T.amber:T.success;
                  rows.push(<tr key={`v-${gt}`} style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:'10px 12px',paddingLeft:multi?24:12,color:T.text,fontWeight:600}}>{dispGt}{oldPrice!=null&&<div title="Sätt priset under Ekonomi per grupp" style={{color:T.textMuted,fontWeight:400,fontSize:11}}>gammalt pris {oldPrice.toLocaleString('sv-SE')} kr – sätts under Ekonomi</div>}</td>
                    <td style={{padding:'10px 12px',textAlign:'center',color:T.text}}>{planned}</td>
                    <td style={{padding:'10px 12px',textAlign:'center',color:T.text}}>{actual}</td>
                    <td style={{padding:'10px 12px',minWidth:120}}>{planned>0?<div style={{display:'flex',alignItems:'center',gap:8}}><div style={{flex:1,height:6,background:T.border,borderRadius:1,overflow:'hidden'}}><div style={{height:'100%',width:`${pct}%`,background:pctColor,borderRadius:1,transition:'width 0.3s'}}/></div><span style={{color:T.textSub,fontSize:11,fontWeight:600,minWidth:36}}>{pct}%</span></div>:null}</td>
                  </tr>);
                }
              }
              return rows;
            })}</tbody>
            <tfoot><tr style={{background:T.surfaceHover}}>
              <td style={{padding:'12px',color:T.text,fontWeight:700}}>Totalt</td>
              <td style={{padding:'12px',textAlign:'center',color:T.text,fontWeight:700}}>{totalPlanned}</td>
              <td style={{padding:'12px',textAlign:'center',color:T.text,fontWeight:700}}>{totalActual}</td>
              {planEditMode&&<td/>}
              {!planEditMode&&<td style={{padding:'12px',color:T.textSub,fontSize:12}}>{totalActual} av {totalPlanned} fällda</td>}
            </tr></tfoot>
          </table>
          {planEditMode&&(isAdmin||isMarkOwner)&&<div style={{marginTop:12,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <input
              value={newSpeciesName}
              onChange={e=>setNewSpeciesName(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter')e.currentTarget.nextElementSibling&&(e.currentTarget.nextElementSibling as HTMLButtonElement).click();}}
              placeholder="Lägg till viltslag (t.ex. Vitsvanshjort)"
              style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,padding:'7px 10px',color:T.text,fontSize:13,fontFamily:'inherit',flex:'1 1 180px'}}
            />
            <button
              onClick={()=>{
                const name=newSpeciesName.trim();
                if(!name)return;
                api.upsertMarkShootingPlan({markId:mark.id,season:planSeason,gameType:name,plannedCount:0})
                  .then(u=>{
                    setPlan(prev=>{const exists=prev.some(p=>p.id===u.id);return exists?prev:([...prev,u]);});
                    const current:string[]=(()=>{try{return JSON.parse((mark as any).gameTypesJson||'[]');}catch{return [];}})();
                    if(!current.includes(name)){
                      const updated=[...current,name];
                      api.updateMark(mark.id,{gameTypesJson:JSON.stringify(updated)}).then((m:any)=>setMark(m)).catch(()=>{});
                    }
                    setNewSpeciesName('');
                  }).catch(()=>{});
              }}
              style={{background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'7px 14px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}
            >+ Lägg till</button>
          </div>}
        </div>;
      })()}
    </div>
  );
}
